import { fetchAdminSettings, patchAdminSettings } from '@/api/admin';
import {
	clearCardFillProxies,
	fetchCardFillStats,
	previewCardFillProxies,
	uploadCardFillProxies,
	type CardFillAdminStats,
	type CardFillProxyPreviewResult,
} from '@/api/admin';
import LoadingWrapper from '@/components/loading-wrapper';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select';
import { formatApiError } from '@/lib/format-api-error';
import type { SenderProxyFormat, SenderProxyProtocol } from '@/entities/sender.entity';
import { ROUTES } from '@/app/routes';
import { CreditCard } from 'lucide-react';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import axios from 'axios';
import SettingsWidgetCard from './settings-widget-card';
import { proxyFormatLabel, proxyProtocolLabel } from '@/pages/admin/sender/proxies/proxy-labels';

function formatPatchError(error: unknown, fallback: string): string {
	if (axios.isAxiosError(error)) {
		return formatApiError(error.response?.data) ?? fallback;
	}
	return fallback;
}

export default function CardFillSettingsWidget() {
	const navigate = useNavigate();
	const [enabled, setEnabled] = useState(false);
	const [dateCheckEnabled, setDateCheckEnabled] = useState(true);
	const [dateCheckHumoEnabled, setDateCheckHumoEnabled] = useState(true);
	const [dateCheckUzcardEnabled, setDateCheckUzcardEnabled] = useState(true);
	const [panResolveEnabled, setPanResolveEnabled] = useState(true);
	const [panResolveUzcardEnabled, setPanResolveUzcardEnabled] = useState(true);
	const [autoOnPhoneResolve, setAutoOnPhoneResolve] = useState(true);
	const [autoPanAfterLookup, setAutoPanAfterLookup] = useState(true);
	const [zipResolveEnabled, setZipResolveEnabled] = useState(true);
	const [launchImportZipPans, setLaunchImportZipPans] = useState(false);
	const [smsProofEnabled, setSmsProofEnabled] = useState(false);
	const [smsProofMode, setSmsProofMode] = useState<'chain_only' | 'balance_verify'>('chain_only');
	const [smsProofDebitOnly, setSmsProofDebitOnly] = useState(true);
	const [maxConcurrent, setMaxConcurrent] = useState(1);
	const [maxDurationSec, setMaxDurationSec] = useState(600);
	const [maxInflight, setMaxInflight] = useState(150);
	const [balanceFetchEnabled, setBalanceFetchEnabled] = useState(false);
	const [balanceRefreshMinutes, setBalanceRefreshMinutes] = useState(240);
	const [balanceLeaseSec, setBalanceLeaseSec] = useState(600);
	const [cardFillMaxReclaims, setCardFillMaxReclaims] = useState(3);
	const [stats, setStats] = useState<CardFillAdminStats | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	const [isSaving, setIsSaving] = useState(false);
	const [protocol, setProtocol] = useState<SenderProxyProtocol>('socks5');
	const [format, setFormat] = useState<SenderProxyFormat>('auto');
	const [selectedFile, setSelectedFile] = useState<File | null>(null);
	const [preview, setPreview] = useState<CardFillProxyPreviewResult | null>(null);
	const [previewError, setPreviewError] = useState<string | null>(null);
	const [isPreviewing, setIsPreviewing] = useState(false);
	const [uploading, setUploading] = useState(false);
	const [clearing, setClearing] = useState(false);
	const previewSeqRef = useRef(0);
	const fileInputRef = useRef<HTMLInputElement>(null);

	async function loadStats() {
		const data = await fetchCardFillStats();
		setStats(data);
	}

	async function runPreview(file: File) {
		const seq = ++previewSeqRef.current;
		setPreview(null);
		setPreviewError(null);
		setIsPreviewing(true);
		try {
			const result = await previewCardFillProxies(file, { protocol, format });
			if (seq !== previewSeqRef.current) {
				return;
			}
			setPreview(result);
			if (result.validCount === 0) {
				toast.error('Не найдено валидных прокси в файле');
			}
		} catch (error) {
			if (seq !== previewSeqRef.current) {
				return;
			}
			setPreviewError(formatPatchError(error, 'Не удалось выполнить preview'));
		} finally {
			if (seq === previewSeqRef.current) {
				setIsPreviewing(false);
			}
		}
	}

	useEffect(() => {
		let cancelled = false;

		async function load() {
			try {
				const settings = await fetchAdminSettings();
				if (cancelled) return;
				setEnabled(settings.cardFillEnabled ?? false);
				setDateCheckEnabled(settings.cardFillDateCheckEnabled ?? true);
				setDateCheckHumoEnabled(settings.cardFillDateCheckHumoEnabled ?? true);
				setDateCheckUzcardEnabled(settings.cardFillDateCheckUzcardEnabled ?? true);
				setPanResolveEnabled(settings.cardFillPanResolveEnabled ?? true);
				setPanResolveUzcardEnabled(settings.cardFillPanResolveUzcardEnabled ?? true);
				setAutoOnPhoneResolve(settings.cardFillAutoOnPhoneResolve ?? true);
				setAutoPanAfterLookup(settings.cardFillAutoPanResolveAfterLookup ?? true);
				setZipResolveEnabled(settings.cardFillZipResolveEnabled ?? true);
				setLaunchImportZipPans(settings.cardFillLaunchImportZipPans ?? false);
				setSmsProofEnabled(settings.cardFillSmsProofEnabled ?? false);
				setSmsProofMode(settings.cardFillSmsProofMode ?? 'chain_only');
				setSmsProofDebitOnly(settings.cardFillSmsProofDebitOnly ?? true);
				setMaxConcurrent(settings.cardFillPanResolveMaxConcurrent ?? 1);
				setMaxDurationSec(settings.cardFillPanResolveMaxDurationSec ?? 600);
				setMaxInflight(settings.cardFillPanResolveMaxInflight ?? 150);
				setBalanceFetchEnabled(settings.cardFillBalanceFetchEnabled ?? false);
				setBalanceRefreshMinutes(settings.cardFillBalanceRefreshIntervalMinutes ?? 240);
				setBalanceLeaseSec(settings.cardFillBalanceLeaseSec ?? 600);
				setCardFillMaxReclaims(settings.cardFillMaxReclaims ?? 3);
				await loadStats();
			} finally {
				if (!cancelled) setIsLoading(false);
			}
		}

		void load();

		return () => {
			cancelled = true;
		};
	}, []);

	useEffect(() => {
		if (!selectedFile) {
			return;
		}
		void runPreview(selectedFile);
	}, [selectedFile, protocol, format]);

	async function handleSubmit(e: FormEvent) {
		e.preventDefault();
		setIsSaving(true);
		try {
			const patchBody: Parameters<typeof patchAdminSettings>[0] = {
					cardFillEnabled: enabled,
					cardFillDateCheckEnabled: dateCheckEnabled,
					cardFillPanResolveEnabled: panResolveEnabled,
					cardFillAutoOnPhoneResolve: autoOnPhoneResolve,
					cardFillAutoPanResolveAfterLookup: autoPanAfterLookup,
					cardFillZipResolveEnabled: zipResolveEnabled,
					cardFillLaunchImportZipPans: launchImportZipPans,
					cardFillSmsProofEnabled: smsProofEnabled,
					cardFillSmsProofMode: smsProofMode,
					cardFillSmsProofDebitOnly: smsProofDebitOnly,
					cardFillPanResolveMaxConcurrent: maxConcurrent,
					cardFillPanResolveMaxDurationSec: maxDurationSec,
					cardFillPanResolveMaxInflight: maxInflight,
					cardFillBalanceFetchEnabled: balanceFetchEnabled,
					cardFillBalanceRefreshIntervalMinutes: balanceRefreshMinutes,
					cardFillBalanceLeaseSec: balanceLeaseSec,
					cardFillMaxReclaims: cardFillMaxReclaims,
			};
			if (dateCheckEnabled) {
				patchBody.cardFillDateCheckHumoEnabled = dateCheckHumoEnabled;
				patchBody.cardFillDateCheckUzcardEnabled = dateCheckUzcardEnabled;
			}
			if (panResolveEnabled) {
				patchBody.cardFillPanResolveUzcardEnabled = panResolveUzcardEnabled;
			}
			await patchAdminSettings(patchBody, { suppressToast: true });
			toast.success('Настройки Card Fill сохранены');
			await loadStats();
		} catch (error) {
			toast.error(formatPatchError(error, 'Не удалось сохранить настройки'));
		} finally {
			setIsSaving(false);
		}
	}

	async function onImport() {
		if (!selectedFile || !preview || preview.validCount === 0) {
			return;
		}
		setUploading(true);
		try {
			const result = await uploadCardFillProxies(selectedFile, { protocol, format });
			toast.success(
				`Добавлено ${result.added}, пропущено ${result.skipped}, невалидных ${result.invalidLines}. В пуле: ${result.proxyCountTotal}`,
			);
			setSelectedFile(null);
			setPreview(null);
			if (fileInputRef.current) {
				fileInputRef.current.value = '';
			}
			await loadStats();
		} catch (error) {
			toast.error(formatPatchError(error, 'Не удалось загрузить прокси'));
		} finally {
			setUploading(false);
		}
	}

	async function onClearPool() {
		if (!window.confirm('Очистить все свободные прокси из пула Card Fill?')) {
			return;
		}
		setClearing(true);
		try {
			const result = await clearCardFillProxies();
			toast.success(`Удалено ${result.removed} прокси. Осталось в пуле: ${result.proxyCountTotal}`);
			await loadStats();
		} catch (error) {
			toast.error(formatPatchError(error, 'Не удалось очистить пул'));
		} finally {
			setClearing(false);
		}
	}

	return (
		<SettingsWidgetCard
			id='card-fill-settings'
			title='Card Fill'
			description='Авто-вбив карт по номеру, дата и PAN resolve. Эндпоинты зафиксированы в коде. Прокси — отдельный пул (merge-only txt).'
			icon={CreditCard}
		>
			<LoadingWrapper show={isLoading}>
				<form className='space-y-4' onSubmit={handleSubmit}>
					<div className='flex items-center justify-between gap-4'>
						<Label htmlFor='card-fill-enabled'>Модуль включён</Label>
						<Switch id='card-fill-enabled' checked={enabled} onCheckedChange={setEnabled} />
					</div>

					<div className='grid gap-3 sm:grid-cols-2'>
						<div className='flex items-center justify-between gap-2'>
							<Label>Date check</Label>
							<Switch checked={dateCheckEnabled} onCheckedChange={setDateCheckEnabled} />
						</div>
						<div className='flex items-center justify-between gap-2 sm:col-span-2 sm:max-w-md'>
							<Label className={!dateCheckEnabled ? 'text-muted-foreground' : undefined}>
								Поиск даты HUMO/VISA
							</Label>
							<Switch
								checked={dateCheckHumoEnabled}
								onCheckedChange={setDateCheckHumoEnabled}
								disabled={!dateCheckEnabled}
							/>
						</div>
						<div className='flex items-center justify-between gap-2 sm:col-span-2 sm:max-w-md'>
							<Label className={!dateCheckEnabled ? 'text-muted-foreground' : undefined}>
								Поиск даты UZCARD
							</Label>
							<Switch
								checked={dateCheckUzcardEnabled}
								onCheckedChange={setDateCheckUzcardEnabled}
								disabled={!dateCheckEnabled}
							/>
						</div>
						<div className='flex items-center justify-between gap-2'>
							<Label>PAN resolve</Label>
							<Switch checked={panResolveEnabled} onCheckedChange={setPanResolveEnabled} />
						</div>
						<div className='flex items-center justify-between gap-2 sm:col-span-2 sm:max-w-md'>
							<Label className={!panResolveEnabled ? 'text-muted-foreground' : undefined}>
								Подбор PAN UZCARD
							</Label>
							<Switch
								checked={panResolveUzcardEnabled}
								onCheckedChange={setPanResolveUzcardEnabled}
								disabled={!panResolveEnabled}
							/>
						</div>
						<div className='flex items-center justify-between gap-2'>
							<Label>Авто lookup по номеру</Label>
							<Switch checked={autoOnPhoneResolve} onCheckedChange={setAutoOnPhoneResolve} />
						</div>
						<div className='flex items-center justify-between gap-2'>
							<Label>Авто PAN после lookup</Label>
							<Switch checked={autoPanAfterLookup} onCheckedChange={setAutoPanAfterLookup} />
						</div>
						<div className='flex items-center justify-between gap-2 sm:col-span-2 sm:max-w-md'>
							<Label className='text-muted-foreground'>
								Legacy: импорт всех PAN из launch ZIP в card
							</Label>
							<Switch checked={launchImportZipPans} onCheckedChange={setLaunchImportZipPans} />
						</div>
						<p className='text-xs text-muted-foreground sm:col-span-2'>
							По умолчанию выкл. (ADR-CF2): кандидаты только в possible_cards; вбив через lookup / ZIP / SMS proof.
						</p>
						<div className='flex items-center justify-between gap-2 sm:col-span-2 sm:max-w-md'>
							<Label>ZIP resolve (маска → PAN из launch ZIP)</Label>
							<Switch checked={zipResolveEnabled} onCheckedChange={setZipResolveEnabled} />
						</div>
						<div className='flex items-center justify-between gap-2 sm:col-span-2 sm:max-w-md'>
							<Label>SMS proof (цепочка date/balance)</Label>
							<Switch checked={smsProofEnabled} onCheckedChange={setSmsProofEnabled} />
						</div>
						<div className='flex flex-col gap-2 sm:col-span-2 sm:max-w-md'>
							<Label className={!smsProofEnabled ? 'text-muted-foreground' : undefined}>Режим SMS proof</Label>
							<Select
								value={smsProofMode}
								onValueChange={v => setSmsProofMode(v as 'chain_only' | 'balance_verify')}
								disabled={!smsProofEnabled}
							>
								<SelectTrigger>
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value='chain_only'>chain_only</SelectItem>
									<SelectItem value='balance_verify'>balance_verify</SelectItem>
								</SelectContent>
							</Select>
						</div>
						<div className='flex items-center justify-between gap-2 sm:col-span-2 sm:max-w-md'>
							<Label className={!smsProofEnabled ? 'text-muted-foreground' : undefined}>
								Только SMS со списанием (Miqdor)
							</Label>
							<Switch
								checked={smsProofDebitOnly}
								onCheckedChange={setSmsProofDebitOnly}
								disabled={!smsProofEnabled}
							/>
						</div>
					</div>

					<div className='grid gap-3 sm:grid-cols-2'>
						<div className='flex items-center justify-between gap-2'>
							<Label>Авто-обновление баланса</Label>
							<Switch checked={balanceFetchEnabled} onCheckedChange={setBalanceFetchEnabled} />
						</div>
					</div>

					<div className='space-y-2'>
						<Label htmlFor='card-fill-balance-refresh'>Интервал авто-обновления баланса (минуты)</Label>
						<Input
							id='card-fill-balance-refresh'
							type='number'
							min={1}
							max={10080}
							value={balanceRefreshMinutes}
							onChange={e => setBalanceRefreshMinutes(Number(e.target.value))}
							disabled={!balanceFetchEnabled}
						/>
						<p className='text-xs text-muted-foreground'>
							Cron проверяет карты раз в минуту; повторный balance_fetch — после истечения интервала с
							момента последнего успешного обновления.
						</p>
					</div>

					<div className='grid gap-3 sm:grid-cols-2'>
						<div className='space-y-2'>
							<Label htmlFor='card-fill-balance-lease'>Lease balance_fetch (сек)</Label>
							<Input
								id='card-fill-balance-lease'
								type='number'
								min={180}
								max={3600}
								value={balanceLeaseSec}
								onChange={e => setBalanceLeaseSec(Number(e.target.value))}
								disabled={!balanceFetchEnabled}
							/>
						</div>
						<div className='space-y-2'>
							<Label htmlFor='card-fill-max-reclaims'>Max reclaims (lease)</Label>
							<Input
								id='card-fill-max-reclaims'
								type='number'
								min={0}
								max={20}
								value={cardFillMaxReclaims}
								onChange={e => setCardFillMaxReclaims(Number(e.target.value))}
							/>
							<p className='text-xs text-muted-foreground'>
								Общий лимит reclaim для всех очередей card-fill (не только balance_fetch).
							</p>
						</div>
					</div>

					<div className='space-y-2'>
						<Label htmlFor='card-fill-concurrent'>PAN concurrent jobs</Label>
						<Input
							id='card-fill-concurrent'
							type='number'
							min={1}
							max={32}
							value={maxConcurrent}
							onChange={e => setMaxConcurrent(Number(e.target.value))}
						/>
						<p className='text-xs text-muted-foreground'>
							Держите 1 — один running job захватывает весь пул прокси.
						</p>
					</div>

					<div className='space-y-2'>
						<Label htmlFor='card-fill-brute-duration'>Лимит брута (сек)</Label>
						<Input
							id='card-fill-brute-duration'
							type='number'
							min={60}
							max={3600}
							value={maxDurationSec}
							onChange={e => setMaxDurationSec(Number(e.target.value))}
						/>
						<p className='text-xs text-muted-foreground'>Дефолт 600 = 10 минут на один pan_resolve.</p>
					</div>

					<div className='space-y-2'>
						<Label htmlFor='card-fill-max-inflight'>Параллельных Plum-запросов</Label>
						<Input
							id='card-fill-max-inflight'
							type='number'
							min={1}
							max={500}
							value={maxInflight}
							onChange={e => setMaxInflight(Number(e.target.value))}
						/>
					</div>

					{stats?.bins ? (
						<div className='text-sm text-muted-foreground space-y-2 rounded-md border p-3'>
							<div className='font-medium text-foreground'>BIN lists (readonly, SSOT: config/card-fill-bins.ts)</div>
							<p className='text-xs'>
								4916 и 4097 идут через Unired phone_lookup; Plum brute для них отключён намеренно.
							</p>
							<div>
								<div className='text-xs uppercase tracking-wide'>
									Date check allowlist (Unired transfer-owner-info, полный PAN)
								</div>
								<div className='font-mono text-xs break-all'>{stats.bins.dateCheckAllowlist.join(', ')}</div>
							</div>
							<div>
								<div className='text-xs uppercase tracking-wide'>
									PAN resolve blocked (Plum brute only — не блокирует Unired phone_lookup)
								</div>
								<div className='font-mono text-xs break-all'>{stats.bins.panResolveBlocked.join(', ')}</div>
							</div>
						</div>
					) : null}

					{stats ? (
						<div className='text-sm text-muted-foreground space-y-1 rounded-md border p-3'>
							<div>Pending: {stats.jobs.pending ?? 0}</div>
							<div>Running: {stats.jobs.running ?? 0}</div>
							<div>Failed: {stats.jobs.failed ?? 0}</div>
							<div>
								Прокси: всего {stats.proxies.total} · свободно {stats.proxies.available} · занято{' '}
								{stats.proxies.assigned}
							</div>
						</div>
					) : null}

					<div className='rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-900 dark:text-amber-200'>
						Исполнитель автовбива: <strong>date_checker_bot</strong>. Прокси для pan_resolve — файл{' '}
						<code className='text-xs'>proxies.txt</code> на хосте бота, не этот пул back.
					</div>

					<div className='space-y-3 rounded-md border p-3'>
						<div className='font-medium text-sm'>Импорт прокси (.txt, merge-only)</div>
						<p className='text-xs text-muted-foreground'>
							Legacy-пул back (card_fill_proxy). При executor=date_checker_bot не используется для brute.
						</p>
						<div className='grid gap-3 sm:grid-cols-2'>
							<div className='space-y-2'>
								<Label>Протокол</Label>
								<Select value={protocol} onValueChange={v => setProtocol(v as SenderProxyProtocol)}>
									<SelectTrigger>
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value='socks5'>{proxyProtocolLabel('socks5')}</SelectItem>
										<SelectItem value='http'>{proxyProtocolLabel('http')}</SelectItem>
									</SelectContent>
								</Select>
							</div>
							<div className='space-y-2'>
								<Label>Формат строк</Label>
								<Select value={format} onValueChange={v => setFormat(v as SenderProxyFormat)}>
									<SelectTrigger>
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value='auto'>{proxyFormatLabel('auto')}</SelectItem>
										<SelectItem value='host_port_user_pass'>{proxyFormatLabel('host_port_user_pass')}</SelectItem>
										<SelectItem value='user_pass_at_host_port'>{proxyFormatLabel('user_pass_at_host_port')}</SelectItem>
										<SelectItem value='url'>{proxyFormatLabel('url')}</SelectItem>
									</SelectContent>
								</Select>
							</div>
						</div>
						<input
							ref={fileInputRef}
							type='file'
							accept='.txt'
							className='block w-full text-sm'
							disabled={uploading || isPreviewing}
							onChange={e => {
								const file = e.target.files?.[0] ?? null;
								setSelectedFile(file);
							}}
						/>
						{isPreviewing ? <div className='text-sm text-muted-foreground'>Проверка файла…</div> : null}
						{previewError ? <div className='text-sm text-destructive'>{previewError}</div> : null}
						{preview ? (
							<div className='text-sm text-muted-foreground space-y-1'>
								<div>Валидных: {preview.validCount}</div>
								<div>Невалидных строк: {preview.invalidLines}</div>
								<div>Дубликатов в файле: {preview.duplicatesRemoved}</div>
								<div>Будет добавлено: {preview.wouldAdd}</div>
								<div>Уже в пуле: {preview.wouldSkip}</div>
								{preview.sample.length > 0 ? (
									<div className='font-mono text-xs break-all'>Пример: {preview.sample.join(', ')}</div>
								) : null}
							</div>
						) : null}
						<div className='flex flex-wrap gap-2'>
							<Button
								type='button'
								variant='secondary'
								disabled={!selectedFile || !preview || preview.validCount === 0 || uploading || isPreviewing}
								onClick={() => void onImport()}
							>
								{uploading ? 'Импорт…' : 'Импортировать'}
							</Button>
							<Button type='button' variant='outline' disabled={clearing} onClick={() => void onClearPool()}>
								{clearing ? 'Очистка…' : 'Очистить свободные'}
							</Button>
						</div>
					</div>

					{stats?.imports && stats.imports.length > 0 ? (
						<div className='space-y-2 rounded-md border p-3'>
							<div className='font-medium text-sm'>Последние импорты</div>
							<div className='space-y-2 text-xs text-muted-foreground'>
								{stats.imports.slice(0, 5).map(item => (
									<div key={item.id} className='border-b border-border/50 pb-2 last:border-0 last:pb-0'>
										<div className='font-medium text-foreground break-all'>{item.filename}</div>
										<div>
											+{item.added} / skip {item.skipped} / invalid {item.invalidLines} · pool{' '}
											{item.proxyCountAfter}
										</div>
										<div>{new Date(item.createdAt).toLocaleString()}</div>
									</div>
								))}
							</div>
						</div>
					) : null}

					<div className='flex flex-wrap gap-2'>
						<Button type='submit' disabled={isSaving}>
							{isSaving ? 'Сохранение…' : 'Сохранить'}
						</Button>
						<Button type='button' variant='outline' size='sm' onClick={() => void loadStats()}>
							Обновить статистику
						</Button>
						<Button
							type='button'
							variant='outline'
							size='sm'
							onClick={() => navigate(ROUTES.adminCardFill, { state: { from: ROUTES.adminSettings } })}
						>
							Мониторинг →
						</Button>
					</div>
				</form>
			</LoadingWrapper>
		</SettingsWidgetCard>
	);
}
